<template>
  <div id="MainContent">
    <h1>Quotes</h1>
    <p v-if="!dataLoaded">Loading&hellip;</p>
  
    <div v-if="dataLoaded">
      <!-- Show Create Quote -->
      <button @click="editMode = !editMode">{{editMode ? 'Cancel' : 'Generate Quote'}}</button>
      <div v-show="editMode">
        <QuoteCreate
          :avail-projects="availProjects"
          :avail-locker-combs="availLockerCombs"
          :avail-products="availProducts"
          :service-fees="service_fees"
          :rto-coefficients="rtoCoefficients"
          @created="onQuoteCreated"
        />
      </div>

      <!-- List All Quotes -->
      <h3 v-show="!editMode">Browse Quotes&hellip;</h3>

      <!-- Use BaseList for search and pagination controls -->
      <BaseList
        v-show="!editMode"
        :search-placeholder="'Search by Quote ID, Project, Customer, or Locker Type...'"
        :filtered-count="filteredQuotes.length"
        :total-count="pagination ? pagination.totalQuotes : quotes.length"
        :current-page="currentPage"
        :items-per-page-prop="quotesPerPage"
        :serverside-pagination="true"
        @search="onBaseSearch"
        @page-change="changePage"
        @items-per-page-change="onItemsPerPageChange"
      />

      <table id="quote-table" v-show="!editMode">
        <tr>
          <th>Quote ID</th>
          <th>Project</th>
          <th>Customer</th>
          <th>Locker Type</th>
          <th>Quote PDF</th>
          <th>Support Agreements</th>
          <th>Trello</th>
        </tr>
        <tr v-for="q in filteredQuotes" :key="q.id">
          <td>QU-{{ String(q.id).padStart(4,'0') }}</td>
          <td>{{q.projectName}}</td>
          <td>{{q.customerName}}</td>
          <td>{{q.locker_name || 'N/A'}}</td>
          <td><a :href="'/'+q.pdf" target="_blank" :alt="'Quote QU-'+String(q.id).padStart(4,'0')">Quote QU-{{ String(q.id).padStart(4,'0') }}</a></td>
          <td class="support-agreements">
            <div class="agreement-links">
              <a v-if="q.sales_agreement_pdf" :href="'/'+q.sales_agreement_pdf" target="_blank" class="agreement-link">Sales Agreement</a><br  v-if="q.sales_agreement_pdf" />
              <a v-if="q.sales_service_agreement_pdf" :href="'/'+q.sales_service_agreement_pdf" target="_blank" class="agreement-link">Sales & Service Agreement</a><br  v-if="q.sales_service_agreement_pdf" />
              <a v-if="q.support_onboarding_agreement_pdf" :href="'/'+q.support_onboarding_agreement_pdf" target="_blank" class="agreement-link">Onboarding Agreement</a><br  v-if="q.support_onboarding_agreement_pdf" />
            </div>
            <button v-if="!(q.sales_agreement_pdf && q.sales_service_agreement_pdf && q.support_onboarding_agreement_pdf)" @click="openSupportAgreementModal(q)" class="add-agreement-btn" title="Add Support Agreement">+</button>
          </td>
          <td>
              <a v-if="q.trelloLink" :href=q.trelloLink target="_blank" >View Card</a>
              <button v-if="!q.trelloLink" @click="pushToTrello(q.id)" class="btn">Send to Trello</button>
          </td>
        </tr>
      </table>

      <!-- Bottom pagination removed; BaseList handles pagination UI -->
            
      <!-- Pagination Info is shown in BaseList; keep minimal info here for fallback -->
      <div v-if="pagination && !editMode" class="pagination-info">
        <p>Showing {{quotes.length}} quotes/page of {{pagination.totalQuotes}} quotes</p>
      </div>

      <!-- Support Agreement Modal -->
      <CreateSupportAgreementModal
        :show="showSupportAgreementModal"
        :quote="selectedQuote"
        @close="closeSupportAgreementModal"
        @agreement-created="onAgreementCreated"
      />

    </div>
  </div>
</template>

<script>
  // Import Helpers
  import { CallApi, EnsureUserLoggedin } from "../Utilities";
  import SearchableDropdown from "../components/SearchableDropdown.vue";
  import QuoteCreate from "../components/QuoteCreate.vue";
  import BaseList from "../components/BaseList.vue";
  import CreateSupportAgreementModal from "../components/CreateSupportAgreementModal.vue";

  // Module Code
  export default {
    components: {
      SearchableDropdown,
      QuoteCreate,
      BaseList,
      CreateSupportAgreementModal
    },
    beforeRouteEnter: EnsureUserLoggedin,
    data: function() {
      return {
        // Generic Data
        editMode: false,
        dataLoaded: false,
        availProjects: [],
        availLockerCombs: [],
        availProducts: [],
  // Existing Quotes
  quotes: [],
  filteredQuotes: [],
  searchText: '',
  searchDebounceTimer: null,
        pagination: null,
        currentPage: 1,
  quotesPerPage: 100,
  // New Quote state handled in QuoteCreate component
        // Rent-to-Own Coefficients
        rtoCoefficients: null,
        // Support Agreement Modal
        showSupportAgreementModal: false,
        selectedQuote: null
      };
    },
    computed: {
      projectsForDropdown: function() {
        return this.availProjects.map(p => ({
          id: p.id,
          displayName: `${p.project} | ${p.customer}`,
          project: p.project,
          customer: p.customer
        }));
      },
      lockersForDropdown: function() {
        return this.availLockerCombs;
      }
    },
    methods: {
      fuzzyMatch: function(searchText, targetText) {
        const search = searchText.toLowerCase().trim();
        const target = targetText.toLowerCase();

        if (!search) return true;

        const searchTerms = search.split(/\s+/).map(term => {
          const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          return `(?:^|[^a-z0-9])${escaped}`;
        });

        const pattern = searchTerms.map(term => `(?=.*${term})`).join('');
        const regex = new RegExp(pattern);

        return regex.test(target);
      },
      // Note: search is performed server-side via BaseList; we keep fuzzyMatch for any local needs
      filterQuotes: function() {
        // kept for backward compatibility; not used when server-side search is active
        if (!this.searchText) {
          this.filteredQuotes = this.quotes;
        } else {
          this.filteredQuotes = this.quotes.filter(q => {
            const quoteId = `QU-${String(q.id).padStart(4,'0')}`;
            const searchableText = `${quoteId} ${q.projectName || ''} ${q.customerName || ''} ${q.locker_name || ''}`;
            return this.fuzzyMatch(this.searchText, searchableText);
          });
        }
      },
      onQuoteCreated() {
        this.fetchData();
        this.editMode = false;
      },
      fetchData: async function(page = null, search = null, limit = null) {
        // Load New Quote Options and RTO Coefficients in parallel
        const [newQuoteOpts, rtoData] = await Promise.all([
          CallApi("GET", "/quotes/new"),
          CallApi("GET", "/renttoown")
        ]);

        this.availProjects = newQuoteOpts.projects;
        this.availLockerCombs = newQuoteOpts.locker_combinations;
        this.availProducts = newQuoteOpts.products;
        this.service_fees = newQuoteOpts.service_fees;
        this.rtoCoefficients = rtoData.coefficients;

        // Use provided page or current page
        const pageToLoad = page || this.currentPage;
        const limitToUse = limit || this.quotesPerPage;
        const searchTerm = (search !== null && search !== undefined) ? search : this.searchText;

        // Load Existing Quotes with pagination and optional server-side search
        let url = `/quotes?page=${pageToLoad}&limit=${limitToUse}`;
        if (searchTerm) {
          url += `&search=${encodeURIComponent(searchTerm)}`;
        }
        const quotesResponse = await CallApi("GET", url);
        this.quotes = quotesResponse.quotes || [];
        this.pagination = quotesResponse.pagination || null;

        // Update current page from response
        if (this.pagination) {
          this.currentPage = this.pagination.currentPage;
        }

        this.quotes.forEach(q => {
          this.availProjects.forEach(p => {
            if (p.id == q.project) {
              q.projectName = p.project;
              q.customerName = p.customer;
            }
          });
        });

  // Initialize filtered quotes
  this.filteredQuotes = this.quotes;

        this.dataLoaded = true;
        this.editMode = false;
      },
      changePage: async function(newPage) {
        if (newPage < 1 || (this.pagination && newPage > this.pagination.totalPages)) {
          return;
        }
        // Clear any pending debounced search to avoid stale requests
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
          this.searchDebounceTimer = null;
        }
        this.currentPage = newPage;
        await this.fetchData(newPage);
      },
      onBaseSearch(searchValue) {
        // Debounce server-side searches by 300ms to reduce request volume while typing
        // Called when BaseList search input changes
        this.searchText = searchValue;
        this.currentPage = 1;

        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
        }

        this.searchDebounceTimer = setTimeout(() => {
          this.searchDebounceTimer = null;
          this.fetchData(1, this.searchText);
        }, 300);
      },
      onItemsPerPageChange(newLimit) {
        // Clear any pending search debounce
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
          this.searchDebounceTimer = null;
        }

        // Update page size and reload first page
        this.quotesPerPage = Number(newLimit) || this.quotesPerPage;
        this.currentPage = 1;
        this.fetchData(1, this.searchText, this.quotesPerPage);
      },
      handleKeydown: function(event) {
        // Only handle keyboard navigation when not in edit mode and pagination exists
        if (this.editMode || !this.pagination || this.pagination.totalPages <= 1) {
          return;
        }

        // Ignore if user is typing in an input field
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.tagName === 'SELECT') {
          return;
        }

        if (event.key === 'ArrowLeft' && this.pagination.hasPrevPage) {
          event.preventDefault();
          this.changePage(this.currentPage - 1);
        } else if (event.key === 'ArrowRight' && this.pagination.hasNextPage) {
          event.preventDefault();
          this.changePage(this.currentPage + 1);
        }
      },
      pushToTrello: async function (id) {
        // Ask DB to Push it to Server
        await CallApi("POST", `/quotes/${id}/trello`, {});
        
        // Reload All Quotes
        this.fetchData();
      },
      openSupportAgreementModal: function(quote) {
        this.selectedQuote = quote;
        this.showSupportAgreementModal = true;
      },
      closeSupportAgreementModal: function() {
        this.showSupportAgreementModal = false;
        this.selectedQuote = null;
      },
      onAgreementCreated: function(response) {
        // Refresh the quotes data to show the new agreement links
        this.fetchData();
      }
    },
    mounted: function() {
      this.fetchData();
      // Add keyboard event listener for pagination navigation
      document.addEventListener('keydown', this.handleKeydown);
    },
    beforeDestroy: function() {
      // Clean up keyboard event listener
      document.removeEventListener('keydown', this.handleKeydown);
      // Clear any pending debounced search
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
    }
  };
</script>

<style scoped>
/* Make columns relative for dropdown positioning */
.column {
  position: relative;
}

</style>